/*----------------------------------
- DOMAINS TITLE BLOCK
----------------------------------*/

/**
 * The page's title row: the H1, its intro paragraph, and the controls the listing owns.
 *
 * Every control opens authentication. Download and Create radar are member tools, Sort is a
 * member tool the public preview hands off (ADR-0029), and Filters only reveals the rail, which
 * is itself a conversion affordance (ADR-0035). They share one geometry so the row reads as one
 * set of controls rather than a CTA with two neighbours.
 */

import Icon from '@/client/components/ui/icon/Icon';
import { PUBLIC_TABLE_SORT_LABEL } from '@/client/catalogs/landing/domains/publicTableColumns';
import { PUBLIC_PREVIEW_BROWSE_CHIPS_LABEL } from '@/client/catalogs/landing/domains/publicPreviewCopy';

// Types
import type { DomainsBrowseChip } from '../lib/browseChips';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsTitleBlockProps = {
    heroTitle: string;
    intro: string | null;
    /** Hub links the phone offers where the desktop keeps the filter rail. */
    browseChips: readonly DomainsBrowseChip[];
    /** Rules already applied to the search on screen, badged on the Filters opener. */
    activeFilterCount: number;
    onOpenFilters: () => void;
    onDownload: () => void;
    onSort: () => void;
    onCreateRadar: () => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const DomainsTitleBlock = ({
    heroTitle,
    intro,
    browseChips,
    activeFilterCount,
    onOpenFilters,
    onDownload,
    onSort,
    onCreateRadar,
}: DomainsTitleBlockProps) => (
    <div class="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div class="min-w-0">
            <h1 class="text-balance text-[clamp(28px,2.6vw,36px)] font-medium leading-[1.12] tracking-[-0.03em] text-slate-900">
                {heroTitle}
            </h1>

            {intro ? (
                <p class="mt-4 max-w-[720px] text-[15.5px] leading-[1.6] text-slate-600">{intro}</p>
            ) : null}
        </div>

        {/*
          * Phone only, and hidden rather than absent from `lg` up, where the filter rail offers
          * the same entry points: a third flex item that never paints leaves the desktop row the
          * two items it has always had. Plain links to crawlable hubs, so nothing here reorders
          * or re-queries the preview below (ADR-0029).
          *
          * Hidden from the stylesheet rather than with `lg:hidden`: domains.css is unlayered and
          * Tailwind v4 emits utilities into `@layer utilities`, so the row's own `display: flex`
          * would beat the utility whatever the breakpoint.
          */}
        {browseChips.length ? (
            <nav
                class="ud-public-domains-scroll-row ud-public-domains-chips"
                aria-label={PUBLIC_PREVIEW_BROWSE_CHIPS_LABEL}
            >
                {browseChips.map((chip) => (
                    <a key={chip.href} href={chip.href} class="ud-public-domains-chip">
                        {chip.label}
                    </a>
                ))}
            </nav>
        ) : null}

        <div class="ud-public-domains-scroll-row items-center">
            {/*
              * The sheet's opener between the two shells: below 1024px the fixed action bar
              * carries Filters, from 1280px the rail is a column with nothing to open, so CSS
              * shows this one only in the band between them. One opener at every width, so the
              * `domains-filters-open` id it shares with the action bar resolves to a single node.
              *
              * Always in the tree rather than behind the viewport state: rendering a different
              * number of controls on the server and on the client made Preact reconcile this
              * row positionally, and it patched the neighbouring labels onto the wrong buttons.
              */}
            <button
                type="button"
                class="ud-public-domains-action ud-public-domains-action--filters"
                data-testid="domains-filters-open-inline"
                onClick={onOpenFilters}
            >
                <Icon name="sliders" size={14} strokeWidth={2} />
                Filters
                {activeFilterCount > 0 ? (
                    <span class="ud-public-domains-action__badge">{activeFilterCount}</span>
                ) : null}
            </button>

            {/* Ordering is a member tool: this opens authentication and never reorders the
                preview, so the crawlable listing keeps one canonical order (ADR-0028). */}
            <button
                type="button"
                class="ud-public-domains-action"
                data-testid="domains-sort"
                onClick={onSort}
            >
                Sort · {PUBLIC_TABLE_SORT_LABEL}
                <Icon name="chevron-down" size={13} strokeWidth={2} />
            </button>

            <button
                type="button"
                class="ud-public-domains-action"
                data-testid="domains-download"
                onClick={onDownload}
            >
                <Icon name="download" size={14} strokeWidth={2} />
                Download
            </button>

            <button
                type="button"
                class="ud-public-domains-action ud-public-domains-action--accent"
                data-testid="domains-create-radar"
                onClick={onCreateRadar}
            >
                <Icon name="radar" size={14} strokeWidth={2} />
                Create radar
            </button>
        </div>
    </div>
);

export default DomainsTitleBlock;
